import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Armchair, Clock, Users, User, Layers, ArrowRightLeft, Merge, Printer, XCircle, ArrowRight, Plus, CalendarClock, Wallet, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, useApi, errMsg } from "@/pos/lib/api";
import { useAuth } from "@/pos/context/AuthContext";
import { rm } from "@/pos/lib/format";
import { useNow, elapsed, clock, TYPE_LABEL } from "@/pos/lib/restaurant";
import CheckoutDialog from "@/pos/components/CheckoutDialog";
import Field from "@/pos/components/Field";

const TONE = {
  available: { card: "border-green-300", box: "bg-green-500", badge: "border-green-300 text-green-600", label: "Kosong" },
  occupied: { card: "border-rose-300", box: "bg-rose-500", badge: "border-rose-300 text-rose-600", label: "Digunakan" },
  reserved: { card: "border-amber-300", box: "bg-amber-400", badge: "border-amber-300 text-amber-600", label: "Ditempah" },
};

function TableCard({ t, active, now, onClick }) {
  const tone = TONE[t.state];
  return (
    <button onClick={onClick} data-testid={`table-card-${t.number}`}
      className={`rounded-2xl border-2 bg-white p-3 text-left transition-[box-shadow,transform] hover:-translate-y-0.5 hover:shadow-md ${tone.card} ${active ? "ring-4 ring-red-100" : ""}`}>
      <div className="flex items-center justify-between">
        <Armchair size={18} className="text-slate-500" />
        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${tone.badge}`}>{tone.label}</span>
      </div>
      <div className="mt-2 flex items-center gap-3">
        <div className={`grid h-16 w-16 shrink-0 place-items-center rounded-xl text-white ${tone.box}`}>
          <div className="text-center leading-none"><div className="font-heading text-2xl font-extrabold">{t.number}</div><div className="mt-1 text-[10px]">Meja</div></div>
        </div>
        <div className="min-w-0 flex-1 space-y-2 text-right text-sm">
          <div className="font-mono font-semibold" data-testid={`table-amount-${t.number}`}>{rm(t.amount)}</div>
          <div className="flex items-center justify-end gap-1 font-mono text-xs text-slate-600"><Clock size={13} />{t.state === "occupied" ? elapsed(t.started_at, now) : "00:00"}</div>
        </div>
      </div>
      <div className="mt-2 text-xs text-slate-500">{t.seats} Kerusi</div>
    </button>
  );
}

function Row({ icon: I, label, value }) {
  return <div className="flex items-center justify-between text-sm"><span className="flex items-center gap-2 text-slate-600"><I size={15} />{label}</span><span className="font-medium">{value}</span></div>;
}

function TableDetail({ t, now, onAction }) {
  const nav = useNavigate();
  const items = t.orders.flatMap((o) => o.items);
  return (
    <div className="space-y-4" data-testid="table-detail">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-bold">Meja {t.number}</h3>
        <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${TONE[t.state].badge}`}>{TONE[t.state].label}</span>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="flex items-center gap-2"><Armchair size={18} className="text-slate-500" /><div><b>{t.seats} Kerusi</b><div className="text-xs text-slate-500">Kapasiti</div></div></div>
        <div className="flex items-center gap-2"><Layers size={18} className="text-slate-500" /><div><b>{t.floor}</b><div className="text-xs text-slate-500">Lantai</div></div></div>
      </div>
      {(t.customer_name || t.customer_phone) && (
        <div className="flex items-center gap-3 rounded-xl bg-slate-50 p-3"><div className="grid h-9 w-9 place-items-center rounded-full bg-red-100 font-bold text-red-700">{(t.customer_name || "?")[0]}</div>
          <div><div className="text-sm font-semibold">{t.customer_name || "-"}</div><div className="text-xs text-slate-500">{t.customer_phone}</div></div></div>
      )}
      {items.length > 0 && (
        <div className="divide-y divide-slate-100 rounded-xl border border-slate-100" data-testid="table-detail-items">
          {items.map((it, i) => (
            <div key={i} className="flex justify-between px-3 py-2 text-sm"><span><span className="mr-2 text-slate-400">{it.qty}×</span>{it.name}{it.note && <span className="block pl-6 text-xs text-amber-700">{it.note}</span>}</span><span className="font-mono">{rm(it.subtotal)}</span></div>
          ))}
          <div className="flex justify-between bg-red-50/60 px-3 py-2.5 font-semibold"><span>Jumlah Besar</span><span className="font-mono text-red-700" data-testid="table-detail-total">{rm(t.amount)}</span></div>
        </div>
      )}
      {t.state === "occupied" && (
        <div className="space-y-2 border-t border-slate-100 pt-3">
          <Row icon={User} label="Pelayan" value={t.waiter || "-"} />
          <Row icon={Clock} label="Mula" value={t.started_at ? clock(t.started_at) : "-"} />
          <Row icon={Clock} label="Tempoh" value={elapsed(t.started_at, now)} />
          <Row icon={Users} label="Tetamu" value={t.guests || "-"} />
        </div>
      )}
      <button onClick={() => nav(`/pos/order?table=${t.id}`)} className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#C81E1E] font-semibold text-white hover:bg-[#1a8741]" data-testid="table-open-order-btn">{t.state === "occupied" ? "Tambah Order" : "Buka Order"} <ArrowRight size={18} /></button>
      {t.state === "occupied" ? (
        <>
          <button onClick={() => onAction("pay")} className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border-2 border-red-600 font-semibold text-red-700 hover:bg-red-50" data-testid="table-pay-btn"><Wallet size={17} /> Bayar & Tutup Meja</button>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <button onClick={() => onAction("transfer")} className="btn-outline !rounded-xl !px-2" data-testid="table-transfer-btn"><ArrowRightLeft size={15} /> Pindah</button>
            <button onClick={() => onAction("merge")} className="btn-outline !rounded-xl !px-2" data-testid="table-merge-btn"><Merge size={15} /> Gabung</button>
            <button onClick={() => window.open(`/pos/cetak/bil/${t.id}`, "_blank")} className="btn-outline !rounded-xl !px-2" data-testid="table-print-bill-btn"><Printer size={15} /> Cetak Bil</button>
          </div>
          {t.orders.length > 0 && <button onClick={() => onAction("cancel")} className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-rose-300 font-semibold text-rose-600 hover:bg-rose-50" data-testid="table-cancel-bill-btn"><XCircle size={17} /> Batal Bil</button>}
        </>
      ) : t.state === "reserved" ? (
        <button onClick={() => onAction("close")} className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-rose-300 font-semibold text-rose-600 hover:bg-rose-50" data-testid="table-close-btn"><XCircle size={17} /> Batal Tempahan</button>
      ) : (
        <button onClick={() => onAction("reserve")} className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-amber-300 font-semibold text-amber-700 hover:bg-amber-50" data-testid="table-reserve-btn"><CalendarClock size={17} /> Tempah Meja</button>
      )}
    </div>
  );
}

function MoveDialog({ mode, table, tables, onClose, onDone }) {
  const merge = mode === "merge";
  const opts = tables.filter((x) => x.id !== table?.id && (merge ? x.state === "occupied" : x.state !== "occupied"));
  const go = async (target) => {
    try { await api.post(`/tables/${table.id}/move`, { target_id: target.id, merge }); toast.success(merge ? "Meja digabung" : "Meja dipindah"); onDone(target.id); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!mode} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="table-move-dialog">
        <DialogHeader><DialogTitle className="font-heading">{merge ? "Gabung" : "Pindah"} Meja {table?.number} ke...</DialogTitle></DialogHeader>
        {opts.length ? <div className="grid grid-cols-4 gap-2">{opts.map((x) => (
          <button key={x.id} onClick={() => go(x)} className={`rounded-xl py-3 font-heading text-lg font-bold text-white ${TONE[x.state].box}`} data-testid={`move-target-${x.number}`}>{x.number}</button>
        ))}</div> : <div className="py-6 text-center text-sm text-slate-500">Tiada meja {merge ? "digunakan" : "kosong"} lain.</div>}
      </DialogContent>
    </Dialog>
  );
}

function ReserveDialog({ table, onClose, onDone }) {
  const [f, setF] = useState({ customer_name: "", customer_phone: "" });
  useEffect(() => setF({ customer_name: "", customer_phone: "" }), [table]);
  const save = async () => {
    try { await api.post(`/tables/${table.id}/state`, { status: "reserved", ...f }); toast.success("Meja ditempah"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!table} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="table-reserve-dialog">
        <DialogHeader><DialogTitle className="font-heading">Tempah Meja {table?.number}</DialogTitle></DialogHeader>
        <Field label="Nama Pelanggan"><Input value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} data-testid="reserve-name-input" /></Field>
        <Field label="No. Telefon"><Input value={f.customer_phone} onChange={(e) => setF({ ...f, customer_phone: e.target.value })} data-testid="reserve-phone-input" /></Field>
        <button onClick={save} disabled={!f.customer_name} className="btn-primary h-11 w-full disabled:opacity-40" data-testid="reserve-save-btn">Simpan Tempahan</button>
      </DialogContent>
    </Dialog>
  );
}

function AddTableDialog({ open, onClose, onDone }) {
  const [f, setF] = useState({ number: "", seats: 4, floor: "Tingkat Bawah" });
  const save = async () => {
    try { await api.post("/tables", { ...f, seats: +f.seats || 1 }); toast.success("Meja ditambah"); setF({ number: "", seats: 4, floor: f.floor }); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="add-table-dialog">
        <DialogHeader><DialogTitle className="font-heading">Meja Baru</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nombor Meja"><Input value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} placeholder="13" data-testid="add-table-number-input" /></Field>
          <Field label="Kerusi"><Input type="number" value={f.seats} onChange={(e) => setF({ ...f, seats: e.target.value })} data-testid="add-table-seats-input" /></Field>
          <div className="col-span-2"><Field label="Lantai"><Input value={f.floor} onChange={(e) => setF({ ...f, floor: e.target.value })} data-testid="add-table-floor-input" /></Field></div>
        </div>
        <button onClick={save} disabled={!f.number} className="btn-primary h-11 w-full disabled:opacity-40" data-testid="add-table-save-btn">Tambah Meja</button>
      </DialogContent>
    </Dialog>
  );
}

function OpenOrders({ orders, onPay, onCancel }) {
  if (!orders.length) return <div className="py-8 text-center text-sm text-slate-500">Pilih meja untuk lihat butiran.</div>;
  return (
    <div className="space-y-2" data-testid="unpaid-orders-list">
      <h3 className="font-heading font-bold">Bungkus / Delivery Belum Bayar</h3>
      {orders.map((o) => (
        <div key={o.id} className="flex items-center justify-between rounded-xl border border-slate-100 p-3 text-sm">
          <div><b className="text-rose-600">#{o.number}</b> · {TYPE_LABEL[o.type]}<div className="text-xs text-slate-500">{o.customer_name || "-"} · {clock(o.created_at)}</div></div>
          <div className="flex items-center gap-1">
            {onCancel && <button onClick={() => onCancel(o)} className="rounded-lg px-2 py-1.5 text-xs text-rose-600 hover:bg-rose-50" data-testid={`unpaid-cancel-${o.number}`}>Batal</button>}
            <button onClick={() => onPay([o])} className="btn-primary !px-3 !py-1.5 text-xs" data-testid={`unpaid-pay-${o.number}`}>Bayar {rm(o.total)}</button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Tables() {
  const { data, reload } = useApi("/tables");
  const { data: openOrders, reload: reloadOpen } = useApi("/orders", { open_only: true });
  const { isOwner } = useAuth();
  const now = useNow(1000);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [selId, setSelId] = useState(null);
  const [move, setMove] = useState(null);
  const [reserve, setReserve] = useState(null);
  const [adding, setAdding] = useState(false);
  const [paying, setPaying] = useState(null);

  useEffect(() => { const t = setInterval(() => { reload(); reloadOpen(); }, 10000); return () => clearInterval(t); }, [reload, reloadOpen]);
  const tables = useMemo(() => data || [], [data]);
  const sel = tables.find((t) => t.id === selId);
  const count = (s) => tables.filter((t) => t.state === s).length;
  const list = useMemo(() => tables.filter((t) => (filter === "all" || t.state === filter) && t.number.toLowerCase().includes(q.toLowerCase())), [tables, filter, q]);
  const refresh = () => { reload(); reloadOpen(); };
  const unpaid = (openOrders || []).filter((o) => o.type !== "dine_in");

  const action = async (a) => {
    if (a === "transfer" || a === "merge") return setMove(a);
    if (a === "reserve") return setReserve(sel);
    if (a === "pay") return setPaying(sel.orders);
    if (a === "cancel") {
      if (!window.confirm(`Batalkan bil Meja ${sel.number}? Semua order belum bayar di meja ini akan dibatalkan.`)) return;
      try { await Promise.all(sel.orders.map((o) => api.post(`/orders/${o.id}/status`, { status: "batal" }))); toast.success("Bil dibatalkan"); refresh(); } catch (e) { toast.error(errMsg(e)); refresh(); }
      return;
    }
    if (a === "close") {
      try { await api.post(`/tables/${sel.id}/state`, { status: "available" }); toast.success("Meja dikosongkan"); refresh(); } catch (e) { toast.error(errMsg(e)); }
    }
  };
  const cancelOrder = async (o) => {
    if (!window.confirm(`Batalkan order #${o.number}?`)) return;
    try { await api.post(`/orders/${o.id}/status`, { status: "batal" }); toast.success("Order dibatalkan"); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const delTable = async () => {
    if (!window.confirm(`Padam Meja ${sel.number}?`)) return;
    try { await api.delete(`/tables/${sel.id}`); setSelId(null); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const chips = [["all", "Semua Meja", tables.length, ""], ["available", "Kosong", count("available"), "bg-green-500"], ["occupied", "Digunakan", count("occupied"), "bg-rose-500"], ["reserved", "Ditempah", count("reserved"), "bg-amber-400"]];
  const payItems = (paying || []).flatMap((o) => o.items);

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-heading text-2xl font-extrabold" data-testid="page-title">Pengurusan Meja</h1>
        <div className="flex gap-2">
          <div className="relative"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari meja" className="h-10 w-48 bg-white pl-9" data-testid="table-search-input" /></div>
          {isOwner && <button onClick={() => setAdding(true)} className="btn-primary" data-testid="add-table-btn"><Plus size={16} /> Meja</button>}
        </div>
      </div>
      <div className="grid gap-5 xl:grid-cols-[1fr_360px]">
        <div className="min-w-0">
          <div className="no-scrollbar mb-4 flex gap-2 overflow-x-auto">
            {chips.map(([k, l, n, dot]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`table-filter-${k}`}
                className={`flex shrink-0 items-center gap-2 rounded-xl border bg-white px-4 py-2 text-sm font-medium transition-colors ${filter === k ? "border-slate-800" : "border-slate-200 text-slate-600"}`}>
                {dot && <span className={`h-2.5 w-2.5 rounded-full ${dot}`} />}{l}<span className="rounded bg-slate-100 px-1.5 text-xs">{n}</span>
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="tables-grid">
            {list.map((t) => <TableCard key={t.id} t={t} now={now} active={t.id === selId} onClick={() => setSelId(t.id)} />)}
          </div>
        </div>
        <aside className="card-surface h-fit p-5 xl:sticky xl:top-6">
          {sel ? <TableDetail t={sel} now={now} onAction={action} /> : <OpenOrders orders={unpaid} onPay={setPaying} onCancel={cancelOrder} />}
          {sel && isOwner && sel.state !== "occupied" && <button onClick={delTable} className="mt-3 flex w-full items-center justify-center gap-1 text-xs text-slate-400 hover:text-rose-600" data-testid="table-delete-btn"><Trash2 size={13} /> Padam meja</button>}
          {sel && unpaid.length > 0 && <button onClick={() => setSelId(null)} className="mt-3 w-full text-xs font-medium text-red-700" data-testid="show-unpaid-btn">Lihat {unpaid.length} order bungkus/delivery belum bayar</button>}
        </aside>
      </div>
      <MoveDialog mode={move} table={sel} tables={tables} onClose={() => setMove(null)} onDone={(id) => { setMove(null); setSelId(id); refresh(); }} />
      <ReserveDialog table={reserve} onClose={() => setReserve(null)} onDone={() => { setReserve(null); refresh(); }} />
      <AddTableDialog open={adding} onClose={() => setAdding(false)} onDone={() => { setAdding(false); refresh(); }} />
      <CheckoutDialog open={!!paying} onClose={() => setPaying(null)} orderIds={(paying || []).map((o) => o.id)}
        cart={payItems.map((i) => ({ product: { id: i.product_id, price: i.price }, qty: i.qty }))}
        subtotal={Math.round(payItems.reduce((s, i) => s + i.subtotal, 0) * 100) / 100} onDone={refresh} />
    </div>
  );
}
